import { LinkClickDto } from '@/Domain/api/Conversation/dto/LinkClick.dto';

/**
 * Utility functions for handling link click events
 */
export class LinkClickHelper {
  /**
   * Extract domain from URL
   */
  static getDomainFromUrl(url: string): string {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  }

  /**
   * Extract domain and path from URL, removing trailing slashes from the path.
   */
  static getDomainAndPathFromUrl(url: string): string {
    try {
      const urlObject = new URL(url);
      let path = urlObject.pathname;
      // Remove trailing slash if it's not the only character in the path
      if (path.length > 1 && path.endsWith('/')) {
        path = path.slice(0, -1);
      }
      // If the path is just a slash, we can consider it empty for display purposes
      if (path === '/') {
        return urlObject.hostname;
      }
      return `${urlObject.hostname}${path}`;
    } catch {
      return url;
    }
  }

  /**
   * Format user agent string to extract browser name
   */
  static formatUserAgent(userAgent: string | null | undefined): string {
    if (!userAgent) return 'Unknown';

    // Extract browser name from user agent
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';

    return 'Unknown Browser';
  }

  /**
   * Get operating system from user agent
   */
  static getOperatingSystem(userAgent: string | null | undefined): string {
    if (!userAgent) return 'Unknown';

    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac OS')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS')) return 'iOS';

    return 'Unknown OS';
  }

  /**
   * Check if the click is from a mobile device
   */
  static isMobileDevice(userAgent: string | null | undefined): boolean {
    if (!userAgent) return false;

    return /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  }

  /**
   * Get click statistics summary for a list of link clicks
   */
  static getClickStatistics(linkClicks: LinkClickDto[]): {
    totalClicks: number;
    uniqueUrls: number;
    topDomains: Array<{ domain: string; count: number }>;
    browserBreakdown: Array<{ browser: string; count: number }>;
    mobileVsDesktop: { mobile: number; desktop: number };
    messageBreakdown: Array<{ messageOrd: number; count: number }>;
  } {
    const domainCounts = new Map<string, number>();
    const browserCounts = new Map<string, number>();
    const messageCounts = new Map<number, number>();
    let mobileClicks = 0;
    let desktopClicks = 0;
    const uniqueUrls = new Set<string>();

    linkClicks.forEach((click) => {
      // Track unique URLs
      uniqueUrls.add(click.original_url);

      // Count domains
      const domain = this.getDomainFromUrl(click.original_url);
      domainCounts.set(domain, (domainCounts.get(domain) || 0) + 1);

      // Count browsers
      const browser = this.formatUserAgent(click.user_agent);
      browserCounts.set(browser, (browserCounts.get(browser) || 0) + 1);

      // Count messages
      messageCounts.set(click.message_ord, (messageCounts.get(click.message_ord) || 0) + 1);

      // Count mobile vs desktop
      if (this.isMobileDevice(click.user_agent)) {
        mobileClicks++;
      } else {
        desktopClicks++;
      }
    });

    return {
      totalClicks: linkClicks.length,
      uniqueUrls: uniqueUrls.size,
      topDomains: Array.from(domainCounts.entries())
        .map(([domain, count]) => ({ domain, count }))
        .sort((a, b) => b.count - a.count),
      browserBreakdown: Array.from(browserCounts.entries())
        .map(([browser, count]) => ({ browser, count }))
        .sort((a, b) => b.count - a.count),
      mobileVsDesktop: { mobile: mobileClicks, desktop: desktopClicks },
      messageBreakdown: Array.from(messageCounts.entries())
        .map(([messageOrd, count]) => ({ messageOrd, count }))
        .sort((a, b) => b.count - a.count),
    };
  }

  /**
   * Find which message generated the most link clicks
   */
  static getMostClickedMessage(
    linkClicks: LinkClickDto[],
  ): { messageOrd: number; clicks: number } | null {
    if (linkClicks.length === 0) return null;

    const messageCounts = new Map<number, number>();
    linkClicks.forEach((click) => {
      messageCounts.set(click.message_ord, (messageCounts.get(click.message_ord) || 0) + 1);
    });

    const sorted = Array.from(messageCounts.entries()).sort((a, b) => b[1] - a[1]);
    return { messageOrd: sorted[0][0], clicks: sorted[0][1] };
  }
}
